import { DatabaseFieldType, type DatabaseTextValue } from '@databases/types/database';
import { createElement, type ReactNode } from 'react';
import { resolveRichTextValue } from '@canvas/adf/resolve-rich-text-value';
import { defineFieldReadModule } from '../field-read-module.ts';
import { createTextFieldReadModule } from '../text/text-field-read-module.ts';
import { RichTextValueView } from './rich-text-value-view.tsx';

export type RichTextReadContent = {
  readonly kind: 'rich-text';
  readonly document: NonNullable<DatabaseTextValue['rich']>;
};
export type RichTextReadRenderer = (content: RichTextReadContent) => ReactNode;
const renderValue: RichTextReadRenderer = (content) =>
  createElement(RichTextValueView, { content });

/**
 * Preserves Text conversions while resolving V1's rich/empty/plain ADF precedence.
 */
export function createRichTextFieldReadModule({
  render = renderValue
}: { readonly render?: RichTextReadRenderer } = {}) {
  const plainModule = createTextFieldReadModule();

  return defineFieldReadModule<RichTextReadContent>({
    fieldType: DatabaseFieldType.TEXT,
    render,
    compile(field) {
      const plain = plainModule.compile(field);

      return Object.freeze({
        fieldId: field.id,
        resolve(storedValue, defaultValue) {
          const value = storedValue ?? defaultValue;
          const document = resolveRichTextValue(
            value?.type === DatabaseFieldType.TEXT
              ? value.value
              : { plain: plain.resolve(storedValue, defaultValue).text }
          );

          return Object.freeze({ kind: 'rich-text' as const, document });
        }
      });
    }
  });
}
